import { useEffect, useMemo, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, Outlet } from 'react-router';
import { EyeOff } from '@linktivity/link-icons';
import {
  Button,
  Dialog,
  Form,
  Input,
  Switch,
  Select
} from '@linktivity/link-ui';
import type { FormValues, Rules, SelectOptionType } from '@linktivity/link-ui';
import { Loader } from '@link/share/components';
import { useDocumentTitle } from '@link/share/hooks';
import { SidebarMenu } from '@link/share/layouts';
import { MemoIcon, LogIcon } from '@link/supplier/assets/icons';
import { SalesStatus } from '@link/supplier/services/const';
import { useStore } from '@link/supplier/stores';
import Header from '../Header';
import styles from './activity.module.css';

const ActivityLayout = () => {
  const { t } = useTranslation();
  const { auth, activity, memo, supplier, activitySupplier } = useStore();
  const [visible, setVisible] = useState(false);
  const [valid, setValid] = useState(true);
  const [adding, setAdding] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState(false);
  const initFormValues = useMemo(() => ({ title: '', sourcePlanId: '' }), []);
  const [formValues, setFormValues] = useState<FormValues>(initFormValues);
  const availablePlans = useMemo<SelectOptionType[]>(
    () =>
      activity.plans.map(plan => ({
        label: plan.title,
        value: plan.id || ''
      })) || [],
    [activity.plans]
  );
  const { activityId } = useParams();

  // 浏览器标签页标题使用当前商品名
  useDocumentTitle(activity.title);

  const rules: Rules = useMemo(
    () => ({
      title: [
        {
          required: true,
          message: t('share.validation.required', {
            name: t('views.plan.name')
          }),
          trigger: ['change', 'blur']
        }
      ],
      sourcePlanId: [
        {
          required: duplicateMode,
          message: t('share.validation.requiredSelect'),
          trigger: ['change', 'blur']
        }
      ]
    }),
    [duplicateMode, t]
  );

  const sortedPlans = useMemo(
    () =>
      activity.plans.sort(
        (planA, planB) =>
          (planB.salesStatus === SalesStatus.On ? 1 : 0) -
          (planA.salesStatus === SalesStatus.On ? 1 : 0)
      ),
    [activity.plans]
  );

  const openForm = useCallback(() => {
    setVisible(true);
    setFormValues(initFormValues);
  }, [initFormValues]);

  const planStatusIcon = useCallback(
    (status: SalesStatus | undefined) =>
      status === SalesStatus.Off ? EyeOff : undefined,
    []
  );

  const menuItems = [
    { label: t('layouts.sidebar.info'), to: 'info' },
    { label: t('layouts.sidebar.detail'), to: 'detail' },
    { label: t('layouts.sidebar.agent'), to: 'agent' },
    { label: t('layouts.sidebar.i18n'), to: 'i18n' },
    ...sortedPlans.map(plan => {
      const archived = plan.salesStatus === SalesStatus.Off;
      return {
        label: t('layouts.sidebar.planInfo', {
          title: plan.title,
          interpolation: { escapeValue: false }
        }),
        archived,
        Icon: planStatusIcon(plan.salesStatus),
        children: [
          {
            label: t('layouts.sidebar.info'),
            to: `${plan.id}/info`,
            archived
          },
          {
            label: t('layouts.sidebar.detail'),
            to: `${plan.id}/detail`,
            archived
          },
          {
            label: t('layouts.sidebar.bookingInfoSettings'),
            to: `${plan.id}/booking`,
            archived
          },
          {
            label: t('layouts.sidebar.i18n'),
            to: `${plan.id}/i18n`,
            archived
          },
          {
            label: t('layouts.sidebar.price'),
            to: `${plan.id}/price`,
            archived
          }
        ]
      };
    }),
    {
      label: t('layouts.sidebar.action'),
      action: (
        <div className={styles.addButton}>
          <Button variant="outlined" size="small" onClick={openForm}>
            {t('layouts.sidebar.addPlan')}
          </Button>
        </div>
      )
    },
    ...(auth.isLinkAdmin
      ? [
          {
            label: t('layouts.sidebar.adminSetting'),
            children: [
              {
                label: t('layouts.sidebar.adminActivity'),
                to: 'admin/activity'
              },
              ...sortedPlans.map(plan => ({
                label: t('layouts.sidebar.adminPlanInfo', {
                  title: plan.title,
                  interpolation: { escapeValue: false }
                }),
                to: `admin/${plan.id}`,
                archived: plan.salesStatus === SalesStatus.Off,
                Icon: planStatusIcon(plan.salesStatus)
              }))
            ]
          }
        ]
      : [])
  ];

  const bottomItems = [
    { label: t('layouts.sidebar.editMemo'), Icon: MemoIcon, to: 'memo' },
    { label: t('layouts.sidebar.editLog'), Icon: LogIcon, to: 'editLog' }
  ];

  const submitForm = useCallback(async (): Promise<void> => {
    const { title, sourcePlanId } = formValues;
    setAdding(true);
    try {
      if (duplicateMode) {
        await activity.duplicatePlan(title, sourcePlanId);
      } else {
        await activity.createPlan(title);
      }
    } finally {
      setVisible(false);
      setAdding(false);
    }
  }, [formValues, duplicateMode, activity]);

  useEffect(() => {
    if (duplicateMode) {
      const { sourcePlanId, title } = formValues;
      setValid(sourcePlanId && !!title);
    } else {
      const { title } = formValues;
      setValid(!!title.length);
    }
  }, [duplicateMode, formValues]);

  useEffect(() => {
    const fetchData = async () => {
      if (activityId) {
        activity.clearActivity();
        await activity.getActivity(activityId);
        if (activity.supplierId) {
          const fetchedSupplier = await supplier.getActivitySupplier(
            activity.supplierId
          );
          if (fetchedSupplier) {
            activitySupplier.setCurrentActivitySupplier(fetchedSupplier);
          }
          // 始终将 selectSupplierId 同步为当前商品所属供应商，确保外链/跨供应商跳转场景下上下文一致
          // 从 fetchedSupplier 取 supplierId（首次请求），缓存命中时从 suppliers 列表查找
          const numericId =
            fetchedSupplier?.supplierId ||
            supplier.getSupplierIdBySupplierCustomId(activity.supplierId);
          if (numericId) {
            supplier.setSelectSupplierId(numericId);
            supplier.setSelectCustomerId(activity.supplierId);
          }
        }
        memo.getMemo(activityId);
      }
    };
    fetchData();
  }, [activity, activityId, activitySupplier, memo, supplier]);

  return (
    <div className={styles.activity}>
      <Header />
      <div className={styles.container}>
        <SidebarMenu menuItems={menuItems} bottomItems={bottomItems} />
        <main className={styles.main}>
          <div className={styles.inner}>
            {activity.loaded ? <Outlet /> : <Loader />}
          </div>
        </main>
        <Dialog
          visible={visible}
          title={t('layouts.sidebar.addPlan')}
          onClose={() => setVisible(false)}
          className={styles.new}
          footer={
            <div className={styles.newFooter}>
              <Button
                type="submit"
                form="newPlan"
                disabled={!valid}
                loading={adding}
              >
                {t('share.common.add')}
              </Button>
            </div>
          }
        >
          <div className={styles.newBody}>
            <Form
              id="newPlan"
              values={formValues}
              onValuesChange={setFormValues}
              onValidateChange={setValid}
              rules={rules}
              onSubmit={submitForm}
            >
              <Form.Item label={t('views.plan.name')} field="title">
                <Input clearable />
              </Form.Item>
              {!!availablePlans.length && (
                <div className={styles.newBodySwitch}>
                  {t('views.plan.duplicatePlan')}
                  <Switch
                    checked={duplicateMode}
                    onChange={({ currentTarget }) =>
                      setDuplicateMode(currentTarget.checked)
                    }
                  />
                </div>
              )}
              {duplicateMode && (
                <Form.Item field="sourcePlanId">
                  <Select
                    className={styles.newBodySelect}
                    options={availablePlans}
                    withinPortal={false}
                  />
                </Form.Item>
              )}
            </Form>
          </div>
        </Dialog>
      </div>
    </div>
  );
};
const ObserverActivityLayout = observer(ActivityLayout);
export default ObserverActivityLayout;
